import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Mail, Loader2, Send, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SendNotificationDialog } from './SendNotificationDialog';
import { supabase } from '@/lib/supabase';
import type { ReportedIssue, NotificationLog } from '@/types';

interface Remark {
  id: string;
  content: string;
  created_at: string;
  created_by: string;
}

const statusColors = {
  new: 'bg-blue-50 text-blue-700 ring-blue-700/10',
  'in review': 'bg-yellow-50 text-yellow-700 ring-yellow-700/10',
  'on hold': 'bg-orange-50 text-orange-700 ring-orange-700/10',
  resolved: 'bg-green-50 text-green-700 ring-green-700/10',
  rejected: 'bg-red-50 text-red-700 ring-red-700/10',
} as const;

const ITEMS_PER_PAGE = 5;

interface IssueSidebarProps {
  issue: ReportedIssue | null;
  notificationLogs: NotificationLog[];
  onUpdate: () => void;
  onClose: () => void;
}

export function IssueSidebar({ issue, notificationLogs, onUpdate, onClose }: IssueSidebarProps) {
  const [status, setStatus] = useState<ReportedIssue['status']>('new');
  const [remarks, setRemarks] = useState('');
  const [remarkHistory, setRemarkHistory] = useState<Remark[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(notificationLogs.length / ITEMS_PER_PAGE);

  // Sort and paginate notification logs
  const paginatedLogs = notificationLogs
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  // Update local state when issue changes
  useEffect(() => {
    if (issue) {
      setStatus(issue.status);
      setRemarks(issue.remarks || '');
      fetchRemarkHistory();
    }
  }, [issue]);

  const fetchRemarkHistory = async () => {
    if (!issue) return;
    try {
      const { data, error } = await supabase
        .from('sms_issue_remarks')
        .select('*')
        .eq('issue_id', issue.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRemarkHistory(data || []);
    } catch (error) {
      console.error('Error fetching remarks:', error);
    }
  };

  const handleNotificationComplete = () => {
    setNotificationDialogOpen(false);
    fetchRemarkHistory();
  };

  if (!issue) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Select an issue to view details
      </div>
    );
  }

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Update issue status and remarks
      const { error: updateError } = await supabase
        .from('sms_reported_issues')
        .update({
          status,
          remarks,
        })
        .eq('id', issue.id);
        
      if (updateError) throw updateError;

      // Add remark to history if provided
      if (remarks.trim()) {
        const { error: remarkError } = await supabase
          .from('sms_issue_remarks')
          .insert({
            issue_id: issue.id,
            content: remarks,
            created_by: 'System User', // Replace with actual user name when auth is implemented
          });

        if (remarkError) throw remarkError;
      }

      toast.success('Issue updated successfully');
      onUpdate();
      fetchRemarkHistory();
    } catch (error) {
      console.error('Error updating issue:', error);
      toast.error('Failed to update issue');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Issue #{issue.issue_number}</h3>
          <p className="text-sm text-muted-foreground">
            {format(new Date(issue.created_at!), 'PPp')}
          </p>
        </div>
        <Badge
          variant="outline"
          className={`${statusColors[issue.status as keyof typeof statusColors]}`}
        >
          {issue.status}
        </Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Update Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Status</label>
            <Select
              value={status}
              onValueChange={(value: ReportedIssue['status']) => setStatus(value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in review">In Review</SelectItem>
                <SelectItem value="on hold">On Hold</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Remarks</label>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add any remarks or notes about this issue"
              rows={4}
            />
          </div>

          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting} 
            className="w-full"
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </CardContent>
      </Card>
      
      <Button
        variant="outline"
        onClick={() => setNotificationDialogOpen(true)}
        className="w-full flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
      >
        <Send className="h-4 w-4" />
        Send Notifications
      </Button>

      {remarkHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Remark History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {remarkHistory.map((remark) => (
                <div key={remark.id} className="border-b pb-4 last:border-0 last:pb-0">
                  <p className="text-sm whitespace-pre-wrap">{remark.content}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <span>{remark.created_by}</span>
                    <span>•</span>
                    <span>{format(new Date(remark.created_at), 'PPp')}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Notification History</CardTitle>
          {notificationLogs.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Showing {Math.min(currentPage * ITEMS_PER_PAGE, notificationLogs.length)} of {notificationLogs.length} notifications
            </p>
          )}
        </CardHeader>
        <CardContent>
          {notificationLogs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No notifications sent yet
            </p>
          ) : (
            <div className="space-y-4">
              {paginatedLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-3 text-sm border-b pb-4 last:border-0 last:pb-0"
                >
                  <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{log.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge
                        variant="outline"
                        className={
                          log.status === 'sent'
                            ? 'bg-green-50 text-green-700 ring-green-700/10'
                            : 'bg-red-50 text-red-700 ring-red-700/10'
                        }
                      >
                        {log.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(log.created_at), 'PPp')}
                      </span>
                    </div>
                    {log.error && (
                      <p className="text-xs text-red-600 mt-1">{log.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {notificationLogs.length > ITEMS_PER_PAGE && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      <SendNotificationDialog
        issue={issue}
        open={notificationDialogOpen}
        onClose={handleNotificationComplete}
      />
    </div>
  );
}